import { defineStore } from 'pinia';
import { api } from '@/services/http.service';
import { ElNotification } from 'element-plus';
import {
  PaymentMethodExceptionLimit,
  ExtendedPaymentMethodExceptionLimit,
  PaymentMethodGeneralLimit,
  PaymentMethodExceptionLimitCreateModel,
  PaymentMethodExceptionLimitUpdateModel,
  PlayerTag,
} from '@/models/ps-management/exception-limit.model';
import { DEFAULT_GEO_PAYMENT_METHOD_FORM, GeoPaymentMethodItem } from '@/models/ps-management/ps-management.model';

interface PaymentMethodsExceptionLimitStore {
  currentGeoPaymentMethod: GeoPaymentMethodItem;
  exceptionLimitList: PaymentMethodExceptionLimit[];
  generalLimitList: PaymentMethodGeneralLimit[];
  isExceptionLimitListLoading: boolean;
  isUpdatingExceptionLimit: boolean;
  isExceptionLimitSettingsSidebarVisible: boolean;
}

export const usePaymentMethodsExceptionLimitStore = defineStore({
  id: 'PaymentMethodsExceptionLimitStore',
  state: (): PaymentMethodsExceptionLimitStore => ({
    currentGeoPaymentMethod: structuredClone(DEFAULT_GEO_PAYMENT_METHOD_FORM),
    exceptionLimitList: [],
    generalLimitList: [],
    isExceptionLimitListLoading: false,
    isUpdatingExceptionLimit: false,
    isExceptionLimitSettingsSidebarVisible: false,
  }),
  getters: {
    extendedPaymentMethodExceptionLimitList(
      state: PaymentMethodsExceptionLimitStore,
    ): ExtendedPaymentMethodExceptionLimit[] {
      return state.generalLimitList
        .map((generalLimit: PaymentMethodGeneralLimit) => {
          const relatedExceptions = this.exceptionLimitList.filter(
            (exception: PaymentMethodExceptionLimit) => exception.currency === generalLimit.currency,
          );

          return {
            ...generalLimit,
            limitList: relatedExceptions,
          };
        });
    },
    tagsByCurrency(state: PaymentMethodsExceptionLimitStore): Record<string, PlayerTag[]> {
      const result: Record<string, PlayerTag[]> = {};

      state.exceptionLimitList.forEach((limit) => {
        if (!result[limit.currency]) {
          result[limit.currency] = [];
        }
        result[limit.currency].push(...limit.playerTags);
      });

      return result;
    },
    currentGeoPaymentMethodId(state: PaymentMethodsExceptionLimitStore): string {
      return state.currentGeoPaymentMethod.billingPaymentMethodSettingsId;
    },
  },
  actions: {
    setExceptionLimitLoading(isLoading: boolean): void {
      this.isExceptionLimitListLoading = isLoading;
    },
    setCurrentGeoPaymentMethod(geoPaymentMethod: GeoPaymentMethodItem): void {
      this.currentGeoPaymentMethod = geoPaymentMethod;
    },
    setUpdatingExceptionLimit(isUpdating: boolean): void {
      this.isUpdatingExceptionLimit = isUpdating;
    },
    getGeoPaymentMethodExceptionLimit(geoPaymentMethodId: string): Promise<PaymentMethodExceptionLimit[]> {
      if (!geoPaymentMethodId) {
        return Promise.resolve(this.exceptionLimitList);
      }

      this.setExceptionLimitLoading(true);

      return api.psManagement.getGeoPaymentMethodExceptionLimitListRequest
        .perform(geoPaymentMethodId)
        .then((exceptionLimitList: PaymentMethodExceptionLimit[]) => {
          this.exceptionLimitList = exceptionLimitList;

          return exceptionLimitList;
        })
        .finally(() => {
          this.setExceptionLimitLoading(false);
        });
    },
    getGeoPaymentMethodGeneralLimit(geoPaymentMethodId: string): Promise<PaymentMethodGeneralLimit[]> {
      if (!geoPaymentMethodId) {
        return Promise.resolve(this.generalLimitList);
      }

      this.setExceptionLimitLoading(true);

      return api.psManagement.getGeoPaymentMethodGeneralLimitListRequest
        .perform(geoPaymentMethodId)
        .then((generalLimitList: PaymentMethodGeneralLimit[]) => {
          this.generalLimitList = generalLimitList;

          return generalLimitList;
        })
        .finally(() => {
          this.setExceptionLimitLoading(false);
        });
    },
    addGeoPaymentMethodExceptionLimit(exceptionLimit: PaymentMethodExceptionLimitCreateModel): Promise<void> {
      this.setUpdatingExceptionLimit(true);

      return api.psManagement.addGeoPaymentMethodExceptionLimitRequest
        .perform({
          paymentMethodSettingsId: this.currentGeoPaymentMethodId,
          paymentMethodExceptionData: exceptionLimit,
        })
        .then(() => {
          this.getGeoPaymentMethodExceptionLimit(this.currentGeoPaymentMethodId);

          ElNotification({
            title: 'Success',
            message: 'Exception limit has been successfully created',
            type: 'success',
          });
        })
        .finally(() => {
          this.setUpdatingExceptionLimit(false);
        });
    },
    updateGeoPaymentMethodExceptionLimit(exceptionLimit: PaymentMethodExceptionLimitUpdateModel): Promise<void> {
      this.setUpdatingExceptionLimit(true);

      return api.psManagement.updateGeoPaymentMethodExceptionLimitRequest
        .perform({
          ...exceptionLimit,
          paymentMethodSettingsId: this.currentGeoPaymentMethodId,
        })
        .then(() => {
          this.getGeoPaymentMethodExceptionLimit(this.currentGeoPaymentMethodId);

          ElNotification({
            title: 'Success',
            message: 'Exception limit has been successfully updated',
            type: 'success',
          });
        })
        .finally(() => {
          this.setUpdatingExceptionLimit(false);
        });
    },
    deleteGeoPaymentMethodExceptionLimit(exceptionLimitId: string): Promise<void> {
      this.setUpdatingExceptionLimit(true);

      return api.psManagement.deleteGeoPaymentMethodExceptionLimitRequest
        .perform({
          paymentMethodSettingsId: this.currentGeoPaymentMethodId,
          exceptionLimitId,
        })
        .then(() => {
          this.getGeoPaymentMethodExceptionLimit(this.currentGeoPaymentMethodId);

          ElNotification({
            title: 'Success',
            message: 'Exception limit has been successfully deleted',
            type: 'success',
          });
        })
        .finally(() => {
          this.setUpdatingExceptionLimit(false);
        });
    },
    setExceptionLimitSettingsSidebarVisible(isVisible: boolean): void {
      this.isExceptionLimitSettingsSidebarVisible = isVisible;
    },
    openExceptionLimitSettings(geoPaymentMethod: GeoPaymentMethodItem): void {
      this.setExceptionLimitSettingsSidebarVisible(true);
      this.setCurrentGeoPaymentMethod(geoPaymentMethod);
    },
    closeExceptionLimitSettings(): void {
      this.setExceptionLimitSettingsSidebarVisible(false);
      this.setCurrentGeoPaymentMethod(structuredClone(DEFAULT_GEO_PAYMENT_METHOD_FORM));
    },
  },
});
